import { useEffect, useState } from 'react'
import type { ActivityPersistentLinkBuilderProps } from '../../../../types/activity.js'
import type { Question, ResonancePresentationMode } from '../../shared/types.js'
import { validateQuestionSet } from '../../shared/validation.js'
import { cacheResonanceQuestionDraft, loadResonanceQuestionDraft } from './resonanceQuestionDraftCache.js'
import ResonanceQuestionSetUploader from './ResonanceQuestionSetUploader.js'

interface PrepareLinkOptionsResponse {
  selectedOptions?: {
    q?: string
    h?: string
    presentationMode?: string
  }
  error?: string
  details?: string[]
}

const PREPARE_LINK_OPTIONS_DEBOUNCE_MS = 500

function normalizePersistentPresentationMode(value: unknown): ResonancePresentationMode {
  return value === 'staged' ? 'staged' : 'standard'
}

function buildPreparedInputSignature(
  teacherCode: string,
  questions: Question[] | null,
  presentationMode: ResonancePresentationMode,
): string | null {
  if (teacherCode.trim().length < 6 || questions === null || questions.length === 0) {
    return null
  }

  return JSON.stringify({
    teacherCode: teacherCode.trim(),
    questions,
    presentationMode,
  })
}

export function normalizeEditStateQuestions(rawSavedQuestions: unknown): Question[] | null {
  const { questions, errors } = validateQuestionSet(rawSavedQuestions)
  if (errors.length > 0 || questions.length === 0) return null
  return questions
}

export function clearPreparedResonanceLinkSelection(
  onSelectedOptionsChange?: (selectedOptions: Record<string, string>) => void,
  onSubmitReadinessChange?: (canSubmit: boolean) => void,
): void {
  onSelectedOptionsChange?.({})
  onSubmitReadinessChange?.(false)
}

/**
 * Activity-owned persistent-link builder for Resonance.
 *
 * Accepts a JSON or Gimkit CSV question set, validates it client-side,
 * posts to /api/resonance/prepare-link-options for server-side validation and
 * encryption, and reports prepared `selectedOptions` plus submit-readiness back
 * to the shared dashboard.
 *
 * Supports both create-mode (no editState) and edit-mode (editState provided).
 * ManageDashboard owns the teacher-code field and final permalink submission.
 * In edit mode the question set is recovered from a local cache keyed by the
 * activity-specific prepared hash when available.
 */
export default function ResonancePersistentLinkBuilder({
  activityId: _activityId,
  teacherCode = '',
  selectedOptions,
  editState,
  onSelectedOptionsChange,
  onSubmitReadinessChange,
}: ActivityPersistentLinkBuilderProps) {
  // Backward compatibility for legacy builder state while preferring local cache.
  const rawSavedQuestions = editState?.selectedOptions?.questions
  const savedQuestionsFromEditState = normalizeEditStateQuestions(rawSavedQuestions)
  const cachedQuestions = typeof editState?.selectedOptions?.h === 'string'
    ? loadResonanceQuestionDraft(editState.selectedOptions.h)
    : (editState?.hash ? loadResonanceQuestionDraft(editState.hash) : null)
  const savedQuestions = savedQuestionsFromEditState ?? cachedQuestions

  const [questions, setQuestions] = useState<Question[] | null>(savedQuestions)
  const [presentationMode, setPresentationMode] = useState<ResonancePresentationMode>(
    normalizePersistentPresentationMode(selectedOptions?.presentationMode ?? editState?.selectedOptions?.presentationMode),
  )
  const [preparing, setPreparing] = useState(false)
  const [prepareError, setPrepareError] = useState<string | null>(null)
  const [preparedHash, setPreparedHash] = useState<string | null>(
    typeof selectedOptions?.h === 'string' ? selectedOptions.h : null,
  )
  const [preparedInputSignature, setPreparedInputSignature] = useState<string | null>(() => {
    const hasPreparedSelectedOptions = typeof selectedOptions?.q === 'string' && typeof selectedOptions?.h === 'string'
    return hasPreparedSelectedOptions ? buildPreparedInputSignature(teacherCode, savedQuestions, presentationMode) : null
  })

  const isEdit = Boolean(editState)
  const normalizedTeacherCode = teacherCode.trim()
  const canPrepare = normalizedTeacherCode.length >= 6 && questions !== null && questions.length > 0
  const currentPreparedInputSignature = buildPreparedInputSignature(normalizedTeacherCode, questions, presentationMode)
  const hasPreparedSelectedOptions = typeof selectedOptions?.q === 'string' && typeof selectedOptions?.h === 'string'
  const isPreparedForCurrentInputs = hasPreparedSelectedOptions
    && currentPreparedInputSignature !== null
    && preparedInputSignature === currentPreparedInputSignature

  const invalidatePreparedState = (): void => {
    setPreparing(false)
    setPrepareError(null)
    setPreparedHash(null)
    setPreparedInputSignature(null)
    clearPreparedResonanceLinkSelection(onSelectedOptionsChange, onSubmitReadinessChange)
  }

  const handleQuestionsChanged = (nextQuestions: Question[] | null): void => {
    invalidatePreparedState()
    setQuestions(nextQuestions)
  }

  const handlePresentationModeChanged = (nextMode: ResonancePresentationMode): void => {
    invalidatePreparedState()
    setPresentationMode(nextMode)
  }

  useEffect(() => {
    let cancelled = false
    const abortController = new AbortController()

    if (!canPrepare || questions === null) {
      invalidatePreparedState()
      return () => {
        cancelled = true
      }
    }

    if (isPreparedForCurrentInputs) {
      setPreparing(false)
      setPrepareError(null)
      setPreparedHash(typeof selectedOptions?.h === 'string' ? selectedOptions.h : null)
      onSubmitReadinessChange?.(true)
      return () => {
        cancelled = true
      }
    }

    onSubmitReadinessChange?.(false)
    setPreparing(true)
    setPrepareError(null)

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        try {
          const resp = await fetch('/api/resonance/prepare-link-options', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ teacherCode: normalizedTeacherCode, questions, presentationMode }),
            signal: abortController.signal,
          })

          const data = (await resp.json()) as PrepareLinkOptionsResponse
          const preparedSelectedOptions = data.selectedOptions
          const preparedHashValue = typeof preparedSelectedOptions?.h === 'string' ? preparedSelectedOptions.h : null
          const preparedQuestionPayload = typeof preparedSelectedOptions?.q === 'string' ? preparedSelectedOptions.q : null

          if (!resp.ok || !preparedHashValue || !preparedQuestionPayload) {
            throw new Error(data.error ?? 'Failed to prepare link options — please try again')
          }

          if (cancelled) {
            return
          }

          cacheResonanceQuestionDraft(preparedHashValue, questions)
          setPreparedHash(preparedHashValue)
          setPreparedInputSignature(currentPreparedInputSignature)
          setPrepareError(null)
          onSelectedOptionsChange?.({
            q: preparedQuestionPayload,
            h: preparedHashValue,
            ...(presentationMode === 'staged' ? { presentationMode } : {}),
          })
          onSubmitReadinessChange?.(true)
        } catch (error) {
          if (cancelled) {
            return
          }
          if ((error instanceof DOMException && error.name === 'AbortError') || abortController.signal.aborted) {
            return
          }

          setPreparedHash(null)
          setPrepareError(error instanceof Error ? error.message : 'Failed to prepare link options — please try again')
          onSelectedOptionsChange?.({})
          onSubmitReadinessChange?.(false)
        } finally {
          if (!cancelled) {
            setPreparing(false)
          }
        }
      })()
    }, PREPARE_LINK_OPTIONS_DEBOUNCE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
      abortController.abort()
    }
  }, [
    canPrepare,
    currentPreparedInputSignature,
    isPreparedForCurrentInputs,
    normalizedTeacherCode,
    onSelectedOptionsChange,
    onSubmitReadinessChange,
    presentationMode,
    questions,
    selectedOptions?.h,
  ])

  return (
    <div className="space-y-5 p-1" aria-label={isEdit ? 'Update Resonance persistent link' : 'Create Resonance persistent link'}>
      <div>
        <p className="block text-sm font-medium text-gray-700 mb-1" id="resonance-plb-file-label">
          Question set
        </p>
        <ResonanceQuestionSetUploader
          onQuestionsChanged={handleQuestionsChanged}
          initialQuestions={savedQuestions}
        />
      </div>

      <div>
        <p className="block text-sm font-medium text-gray-700 mb-1" id="resonance-presentation-mode-label">
          Presentation mode
        </p>
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" aria-labelledby="resonance-presentation-mode-label">
          {(['standard', 'staged'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={presentationMode === mode}
              onClick={() => handlePresentationModeChanged(mode)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                presentationMode === mode
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {mode === 'standard' ? 'Standard' : 'Staged'}
            </button>
          ))}
        </div>
      </div>

      {normalizedTeacherCode.length > 0 && normalizedTeacherCode.length < 6 && (
        <p className="text-sm text-amber-700" role="status">
          Teacher code must be at least 6 characters before Resonance can prepare the link payload.
        </p>
      )}

      {prepareError && (
        <p className="text-sm text-red-600" role="alert">
          {prepareError}
        </p>
      )}

      {!prepareError && canPrepare && preparing && (
        <p className="text-sm text-gray-600" role="status">
          Preparing encrypted question payload…
        </p>
      )}

      {!prepareError && !preparing && preparedHash && (
        <p className="text-sm text-green-700" role="status">
          Question set ready. You can now {isEdit ? 'save' : 'generate'} the link.
        </p>
      )}
    </div>
  )
}
